// src/app/features/conversations/FloatingChat/FloatingChatButton.tsx
import React, { useEffect } from 'react';
import { useDraggable } from './useDraggable';
import { useGetNotificationsCounterQuery } from 'app/services/notifications';
import { oneSecond } from '../Hooks/useElapsedTimeFromNow';
import { useAuth } from 'utils/AuthProvider';

export interface FloatingChatButtonProps {
  isOpen: boolean;
  onClick: () => void; // called if the user "clicks" the button
  initialOffset?: { x: number; y: number }; // optional starting position
}

export default function FloatingChatButton({
  isOpen,
  onClick,
  initialOffset = { x: 80, y: 80 },
}: FloatingChatButtonProps) {
  // Use the custom hook
  const { pos, isClick, onPointerDown, onPointerMove, onPointerUp, setPos } =
    useDraggable({
      buttonSize: 30,
      clickThreshold: 5, // px
    });

  const { user } = useAuth();
  const { data } = useGetNotificationsCounterQuery(undefined, {
    pollingInterval: 20 * oneSecond,
    skip: !user,
  });
  const unreadCount = data?.unread_messages_count ?? 0;

  // Place the button near bottom-right on mount
  useEffect(() => {
    if (pos.x === 0 && pos.y === 0) {
      setPos({
        x: window.innerWidth - initialOffset.x,
        y: window.innerHeight - initialOffset.y,
      });
    }
  }, [pos, setPos, initialOffset]);

  // On pointerUp, if the user didn't really drag => "click"
  const handlePointerUp: React.PointerEventHandler<HTMLButtonElement> = (e) => {
    onPointerUp(e);
    if (isClick) {
      onClick();
    }
  };

  // Style for the floating button
  const chatButtonStyle: React.CSSProperties = {
    position: 'fixed',
    left: pos.x,
    top: pos.y,
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    backgroundColor: 'rgba(57, 190, 157, 0.8)',
    color: '#fff',
    fontSize: '1.25rem',
    fontWeight: 'bold',
    border: 'none',
    outline: 'none',
    boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none', // prevents text highlight
    cursor: 'pointer',
    zIndex: 9999,
  };

  return (
    <button
      style={chatButtonStyle}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={handlePointerUp}
    >
      {isOpen ? '✕' : '💬'}
      {!isOpen && unreadCount > 0 && (
        <span
          style={{
            position: 'absolute',
            top: -4,
            right: -4,
            backgroundColor: '#dc2626',
            color: '#fff',
            borderRadius: '50%',
            width: 16,
            height: 16,
            fontSize: '0.65rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </button>
  );
}
